/**
 * AI 分析 · 入口页
 *
 * 调用链：
 *   1) handleStartTask
 *      → createAITask({ aiTaskType: 100, input: { urls, edit_user } })
 *      → 拿到 taskID
 *   2) pollAITaskResult({ taskID, userID })
 *      → 内部以 2s 间隔轮询直到 taskStatus 为 3（成功）或 4（失败）
 *      → 拿到 output URL
 *   3) fetchOutputFromUrl<MingyangAiResult>(output)
 *      → 解析得到 { res: { rules, all_compliant, retrieval, warnings, ... } }
 *
 * 团队照片选择：跳转 /photo/picker?from=mingyangAi&max=30，
 * 选完通过 state.photoPicker.backFromPickerPage 通信回来。
 *
 * 权限：仅 ALLOWED_GROUP_IDS 命中的团队可访问。
 */
import './index.scss'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, message } from 'antd'
import {
  BookOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
  ExclamationCircleFilled,
  FileProtectOutlined,
  LeftOutlined,
  NumberOutlined,
  PictureOutlined,
  PlayCircleFilled,
  ReloadOutlined,
  RightOutlined,
  WarningOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import {
  setBackFromPickerPage,
  setPhotoPickerPhotos
} from '../../actions/photoPicker'
import { useAITaskEvent } from '../watermark/batchEditor/hooks/useAITaskEvent'
import {
  MingyangAiPhoto,
  MingyangAiResult,
  MingyangAiResultRes,
  MINGYANG_AI_TASK_TYPE
} from './types'
import {
  PICKER_FROM_FLAG,
  PICKER_MAX_COUNT,
  isMingyangAiAllowed
} from './constants'

type PageStatus = 'idle' | 'starting' | 'polling' | 'completed' | 'failed'

const MingyangAiPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // —— 团队信息 ——
  const groupId = useAppSelector(
    (state) => state.group.groupId as string | undefined
  )
  const userId = useAppSelector(
    (state) => state.main.user?.user_id as string | undefined
  )
  const allowed = useMemo(() => isMingyangAiAllowed(groupId), [groupId])

  // —— PhotoPickerPage 回填通信 ——
  const backFromPicker = useAppSelector(
    (state) => (state.photoPicker as any).backFromPickerPage
  ) as boolean
  const pickerFrom = useAppSelector(
    (state) => (state.photoPicker as any).from
  ) as string | undefined
  const pickerPhotos = useAppSelector(
    (state) => (state.photoPicker as any).photos
  ) as any

  // —— 仓内现成 AI 任务工具 ——
  const { createAITask, pollAITaskResult } = useAITaskEvent()

  // —— 状态机 ——
  const [status, setStatus] = useState<PageStatus>('idle')
  const [statusLabel, setStatusLabel] = useState<string>('')
  const [taskId, setTaskId] = useState<string>('')
  const [photoCount, setPhotoCount] = useState<number>(0)
  /** 本次分析选中的照片，用于结果页展示缩略图 + 大图预览 */
  const [analysisPhotos, setAnalysisPhotos] = useState<MingyangAiPhoto[]>([])
  const [result, setResult] = useState<MingyangAiResultRes | null>(null)
  const [errorMsg, setErrorMsg] = useState<string>('')

  // —— 清理 photoPicker redux 状态 ——
  const clearPickerState = useCallback(() => {
    dispatch(setBackFromPickerPage(false))
    dispatch(setPhotoPickerPhotos([], null))
  }, [dispatch])

  // —— 发起 AI 任务（完整流程：create → poll → fetchOutput） ——
  const handleStartTask = useCallback(
    async (photos: MingyangAiPhoto[]) => {
      if (!userId) {
        message.error('未获取到用户信息')
        setStatus('idle')
        return
      }

      setStatus('starting')
      setStatusLabel('正在创建分析任务')
      setPhotoCount(photos.length)
      setAnalysisPhotos(photos)
      setTaskId('')
      setResult(null)
      setErrorMsg('')

      const urls = photos.map((p) => p.url).filter(Boolean)
      if (urls.length === 0) {
        setStatus('failed')
        setErrorMsg('未拿到照片 URL')
        return
      }

      try {
        // 1) 创建 AI 任务
        const taskResult = await createAITask({
          aiTaskType: MINGYANG_AI_TASK_TYPE,
          input: { urls, edit_user: userId },
          userID: userId
        })
        const taskID = taskResult?.data?.taskID
        if (!taskID) {
          throw new Error('创建任务后未返回 taskID')
        }
        setTaskId(taskID)
        setStatus('polling')
        setStatusLabel('AI 分析中')

        // 2) 轮询任务状态（pollAITaskResult 内部 2s 间隔，等到 taskStatus 3/4）
        const pollResult = await pollAITaskResult(
          { taskID, userID: userId },
          { photoCount: urls.length }
        )
        const taskStatus = pollResult?.data?.taskStatus
        if (taskStatus === 4) {
          throw new Error('AI 任务处理失败')
        }
        if (taskStatus === 5) {
          throw new Error('AI 任务超时未完成')
        }
        if (taskStatus !== 3) {
          throw new Error(`任务状态异常：${taskStatus}`)
        }

        const rawOutput = pollResult?.data?.output
        if (!rawOutput) {
          throw new Error('任务完成但未返回结果')
        }

        // 3) 解析结果 —— output 直接是 JSON 字符串，不是 URL
        setStatusLabel('正在解析结果')
        let fullResult: MingyangAiResult
        try {
          fullResult =
            typeof rawOutput === 'string'
              ? (JSON.parse(rawOutput) as MingyangAiResult)
              : (rawOutput as MingyangAiResult)
        } catch (parseErr) {
          throw new Error(
            '结果解析失败：' + ((parseErr as Error)?.message || 'JSON 格式异常')
          )
        }
        if (!fullResult || fullResult.success !== 1) {
          throw new Error(
            fullResult?.show_reason || fullResult?.reason || '结果解析失败'
          )
        }

        setResult(fullResult.res)
        setStatus('completed')
      } catch (e) {
        setStatus('failed')
        setErrorMsg((e as Error)?.message || '分析失败')
      }
    },
    [createAITask, pollAITaskResult, userId]
  )

  // —— 监听 PhotoPickerPage 返回 ——
  useEffect(() => {
    if (!backFromPicker) return
    if (pickerFrom !== PICKER_FROM_FLAG) return

    const raw: any[] = Array.isArray(pickerPhotos) ? pickerPhotos : []
    // 注意：picker 返回的 photo 对象字段为驼峰 —— largeURL / smallURL /
    // mediaType / seq（aiSecurity 等模块亦如此用，见 pages/aiSecurity/index.tsx:235）
    const photos: MingyangAiPhoto[] = raw.map((p: any) => ({
      seq: p.seq,
      url: p.largeURL || p.photo_url || p.url || '',
      small_url: p.smallURL || p.small_url || p.small_photo_url || '',
      media_type: p.mediaType ?? p.media_type ?? 0
    }))
    if (photos.length > 0 && !photos[0].url) {
      // eslint-disable-next-line no-console
      console.warn(
        '[mingyangAi] picker 返回的照片对象没有 largeURL 字段，请检查',
        raw[0]
      )
    }

    clearPickerState()

    if (photos.length === 0) {
      message.warning('未选择照片')
      return
    }
    handleStartTask(photos)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backFromPicker, pickerFrom])

  // —— 跳转 picker ——
  const handleOpenPicker = useCallback(() => {
    const qs = `?from=${PICKER_FROM_FLAG}&max=${PICKER_MAX_COUNT}&mediaType=0&pageTitle=${encodeURIComponent(
      '选择团队照片'
    )}`
    navigate(`/photo/picker${qs}`)
  }, [dispatch])

  const handleReset = useCallback(() => {
    setStatus('idle')
    setStatusLabel('')
    setTaskId('')
    setPhotoCount(0)
    setAnalysisPhotos([])
    setResult(null)
    setErrorMsg('')
  }, [])

  // —— 未授权 ——
  if (!allowed) {
    return (
      <div className="mingyang-ai">
        <div className="mingyang-ai__forbidden">
          <WarningOutlined />
          <h2>暂无权限访问</h2>
          <p>AI 分析仅向授权团队开放，请联系管理员。</p>
        </div>
      </div>
    )
  }

  return (
    <div className="mingyang-ai">
      {/* 顶部页头 */}
      <div className="mingyang-ai__page-header">
        <h1 className="mingyang-ai__page-title">AI 分析</h1>
        <div className="mingyang-ai__page-subtitle">
          基于团队照片的智能识别与分析
        </div>
      </div>

      {/* idle: hero 卡片 */}
      {status === 'idle' && (
        <div className="mingyang-ai__hero">
          <div className="mingyang-ai__hero-illu">
            <FileProtectOutlined />
          </div>
          <div className="mingyang-ai__hero-body">
            <h2 className="mingyang-ai__hero-title">团队照片智能分析</h2>
            <p className="mingyang-ai__hero-desc">
              从团队相册中选择照片，AI 将自动匹配检查项知识库、识别照片内的关键
              检查规则是否合规，并生成可追溯的分析报告。
            </p>
            <div className="mingyang-ai__hero-features">
              <span className="mingyang-ai__hero-feature">
                <CheckCircleFilled />
                自动匹配检查项
              </span>
              <span className="mingyang-ai__hero-feature">
                <CheckCircleFilled />
                逐条规则判定
              </span>
              <span className="mingyang-ai__hero-feature">
                <CheckCircleFilled />
                结构化报告
              </span>
            </div>
            <Button
              type="primary"
              className="mingyang-ai__hero-cta"
              onClick={handleOpenPicker}
            >
              <PictureOutlined />
              选择团队照片，开始智能分析
            </Button>
          </div>
        </div>
      )}

      {/* starting / polling — indeterminate spinner */}
      {(status === 'starting' || status === 'polling') && (
        <div className="mingyang-ai__task">
          <div className="mingyang-ai__task-spinner">
            <div className="mingyang-ai__task-spinner-ring" />
            <div className="mingyang-ai__task-spinner-icon">
              <FileProtectOutlined />
            </div>
          </div>
          <div className="mingyang-ai__task-status">
            {statusLabel || '准备中'}
            <span className="mingyang-ai__task-dots">
              <span />
              <span />
              <span />
            </span>
          </div>
          <div className="mingyang-ai__task-hint">
            AI 正在分析您选择的 {photoCount} 张照片，请稍候。处理时间会随照片
            数量增加。
          </div>
          {taskId && (
            <div className="mingyang-ai__task-meta">
              <NumberOutlined /> {taskId}
            </div>
          )}
          <div className="mingyang-ai__task-actions">
            <Button onClick={handleReset}>取消并返回</Button>
          </div>
        </div>
      )}

      {/* completed */}
      {status === 'completed' && result && (
        <ResultView
          res={result}
          photos={analysisPhotos}
          onReset={handleReset}
          onRetry={handleOpenPicker}
        />
      )}

      {/* failed */}
      {status === 'failed' && (
        <div className="mingyang-ai__failed">
          <CloseCircleFilled />
          <div className="mingyang-ai__task-status">分析失败</div>
          <div className="mingyang-ai__task-hint">
            {errorMsg || '请稍后重试。'}
          </div>
          <div className="mingyang-ai__task-actions">
            <Button onClick={handleReset}>返回</Button>
            <Button type="primary" onClick={handleOpenPicker}>
              <ReloadOutlined />
              重新尝试
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================
// 结果展示子组件
// ============================================================

interface ResultViewProps {
  res: MingyangAiResultRes
  photos: MingyangAiPhoto[]
  onReset: () => void
  onRetry: () => void
}

const ResultView: React.FC<ResultViewProps> = ({
  res,
  photos,
  onReset,
  onRetry
}) => {
  const compliantCount = res.rules.filter((r) => r.is_compliant).length
  const totalCount = res.rules.length
  const violationCount = totalCount - compliantCount
  const objects = res.objects || []

  // —— 大图 / 视频预览状态 ——
  const [previewIndex, setPreviewIndex] = useState<number>(-1)

  return (
    <div className="mingyang-ai__result">
      {/* 整体合规判定 —— 左侧缩略图组 + 右侧文字判定 + 底部命中检查项 */}
      <div
        className={
          'mingyang-ai__verdict ' +
          (res.all_compliant
            ? 'mingyang-ai__verdict--pass'
            : 'mingyang-ai__verdict--fail')
        }
      >
        {photos.length > 0 ? (
          <PhotoThumbs photos={photos} onPreview={setPreviewIndex} />
        ) : (
          <div className="mingyang-ai__verdict-icon">
            {res.all_compliant ? <CheckCircleFilled /> : <CloseCircleFilled />}
          </div>
        )}
        <div className="mingyang-ai__verdict-body">
          <div className="mingyang-ai__verdict-title">
            {res.all_compliant ? (
              <CheckCircleFilled className="mingyang-ai__verdict-title-icon" />
            ) : (
              <CloseCircleFilled className="mingyang-ai__verdict-title-icon" />
            )}
            {res.all_compliant ? '整体合规' : '存在不合规项'}
            <span className="mingyang-ai__verdict-stats">
              · 共 {totalCount} 条规则
              {compliantCount > 0 && ` · 合规 ${compliantCount}`}
              {violationCount > 0 && ` · 不合规 ${violationCount}`}
            </span>
          </div>
          {res.retrieval?.hit && res.retrieval.knowledge_inspection && (
            <div className="mingyang-ai__verdict-tag">
              <BookOutlined />
              命中检查项：{res.retrieval.knowledge_inspection}
            </div>
          )}
        </div>
      </div>

      {/* 大图 / 视频 Lightbox 预览 */}
      {previewIndex >= 0 && photos[previewIndex] && (
        <PhotoLightbox
          photos={photos}
          index={previewIndex}
          onClose={() => setPreviewIndex(-1)}
          onPrev={() => setPreviewIndex((i) => Math.max(0, i - 1))}
          onNext={() =>
            setPreviewIndex((i) => Math.min(photos.length - 1, i + 1))
          }
        />
      )}

      {/* warnings 警告（仅当存在） */}
      {res.warnings && res.warnings.length > 0 && (
        <div className="mingyang-ai__warnings">
          <div className="mingyang-ai__warnings-title">
            <ExclamationCircleFilled />
            注意（{res.warnings.length}）
          </div>
          {res.warnings.map((w, i) => (
            <div key={i} className="mingyang-ai__warnings-item">
              · {w}
            </div>
          ))}
        </div>
      )}

      {/* 识别对象 / 部件（仅当存在） */}
      {objects.length > 0 && (
        <div className="mingyang-ai__objects">
          <div className="mingyang-ai__objects-title">
            识别对象（{objects.length}）
          </div>
          <div className="mingyang-ai__objects-grid">
            {objects.map((obj, i) => (
              <div key={i} className="mingyang-ai__object">
                <div className="mingyang-ai__object-name">{obj.name}</div>
                <div className="mingyang-ai__object-value">{obj.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 规则检查列表 */}
      <div className="mingyang-ai__rules">
        <div className="mingyang-ai__rules-title">规则检查（{totalCount}）</div>
        {res.rules.length === 0 && (
          <div className="mingyang-ai__rules-empty">无规则数据</div>
        )}
        {res.rules.map((rule, i) => (
          <div
            key={i}
            className={
              'mingyang-ai__rule ' +
              (rule.is_compliant
                ? 'mingyang-ai__rule--pass'
                : 'mingyang-ai__rule--fail')
            }
          >
            <div className="mingyang-ai__rule-icon">
              {rule.is_compliant ? (
                <CheckCircleFilled />
              ) : (
                <CloseCircleFilled />
              )}
            </div>
            <div className="mingyang-ai__rule-body">
              <div className="mingyang-ai__rule-name">{rule.name}</div>
              <div className="mingyang-ai__rule-reason">{rule.reason}</div>
            </div>
            <div className="mingyang-ai__rule-tag">
              {rule.is_compliant ? '合规' : '不合规'}
            </div>
          </div>
        ))}
      </div>

      <div className="mingyang-ai__result-actions">
        <Button onClick={onReset}>返回</Button>
        <Button type="primary" onClick={onRetry}>
          <ReloadOutlined />
          重新选择照片分析
        </Button>
      </div>
    </div>
  )
}

// ============================================================
// 缩略图组 + 大图 / 视频 Lightbox 预览
// ============================================================

function isVideoPhoto(p: MingyangAiPhoto): boolean {
  if (p.media_type === 1) return true
  const url = (p.url || '').toLowerCase()
  return /\.(mp4|mov|webm|m3u8|avi|mkv|m4v|3gp)(\?|$|#)/.test(url)
}

/**
 * 视频播放 URL 处理：picker 返回的视频 URL 通常带 OSS 视频截图参数
 * （?x-oss-process=video/snapshot,t_0,m_fast,ar_auto），这种 URL 返回的
 * 是首帧 JPG 而不是视频文件。缩略图直接用此 URL 当 <img> 展示首帧没问题，
 * 但 <video> 播放必须删掉这个 query，否则解码失败黑屏。
 */
function getVideoPlayUrl(url: string): string {
  if (!url) return url
  try {
    const u = new URL(url)
    u.searchParams.delete('x-oss-process')
    return u.toString()
  } catch {
    // 非合法 URL 兜底：粗暴去掉 query
    return url.split('?')[0]
  }
}

const PhotoThumbs: React.FC<{
  photos: MingyangAiPhoto[]
  onPreview: (idx: number) => void
}> = ({ photos, onPreview }) => {
  // 网格最多展示 4 张；> 4 时第 4 格作为 "+N" 提示，点击预览第 4 张起
  const visibleCount = Math.min(4, photos.length)
  const rest = photos.length - visibleCount

  return (
    <div className="mingyang-ai__thumbs">
      {photos.slice(0, visibleCount).map((p, i) => {
        const isVideo = isVideoPhoto(p)
        const isLastWithRest = i === visibleCount - 1 && rest > 0
        return (
          <div
            key={`${p.seq}-${i}`}
            className="mingyang-ai__thumb"
            onClick={() => onPreview(i)}
          >
            {isVideo ? (
              <>
                {/* OSS snapshot URL 返回的是首帧 JPG，<img> 直接展示即可 */}
                <img
                  src={p.small_url || p.url}
                  alt=""
                  className="mingyang-ai__thumb-media"
                />
                <span className="mingyang-ai__thumb-play">
                  <PlayCircleFilled />
                </span>
              </>
            ) : (
              <img
                src={p.small_url || p.url}
                alt=""
                className="mingyang-ai__thumb-media"
              />
            )}
            {isLastWithRest && (
              <span className="mingyang-ai__thumb-rest">+{rest}</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

const PhotoLightbox: React.FC<{
  photos: MingyangAiPhoto[]
  index: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
}> = ({ photos, index, onClose, onPrev, onNext }) => {
  const item = photos[index]

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') onPrev()
      else if (e.key === 'ArrowRight') onNext()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, onPrev, onNext])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  if (!item) return null
  const isVideo = isVideoPhoto(item)
  const hasPrev = index > 0
  const hasNext = index < photos.length - 1

  return (
    <div className="mingyang-ai__lightbox" onClick={onClose}>
      {/* 顶部条 */}
      <div
        className="mingyang-ai__lightbox-top"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="mingyang-ai__lightbox-counter">
          {index + 1} / {photos.length}
        </span>
        <span
          className="mingyang-ai__lightbox-close"
          onClick={onClose}
          title="关闭 (Esc)"
        >
          <CloseOutlined />
        </span>
      </div>

      {/* 中央内容 */}
      <div
        className="mingyang-ai__lightbox-body"
        onClick={(e) => e.stopPropagation()}
      >
        {isVideo ? (
          <video
            src={getVideoPlayUrl(item.url)}
            controls
            autoPlay
            className="mingyang-ai__lightbox-media"
          />
        ) : (
          <img src={item.url} alt="" className="mingyang-ai__lightbox-media" />
        )}
      </div>

      {/* 左右切换 */}
      {hasPrev && (
        <span
          className="mingyang-ai__lightbox-arrow mingyang-ai__lightbox-arrow--prev"
          onClick={(e) => {
            e.stopPropagation()
            onPrev()
          }}
        >
          <LeftOutlined />
        </span>
      )}
      {hasNext && (
        <span
          className="mingyang-ai__lightbox-arrow mingyang-ai__lightbox-arrow--next"
          onClick={(e) => {
            e.stopPropagation()
            onNext()
          }}
        >
          <RightOutlined />
        </span>
      )}
    </div>
  )
}

export default MingyangAiPage
